import {
  AlertCircle,
  AlertTriangle,
  RefreshCw,
  type LucideIcon
} from "lucide-react";
import { cn } from "@/utilities/tailwind-utils";
import { Button } from "./button";
import { Card, CardContent } from "./card";

type ErrorDisplayVariant = "default" | "destructive" | "warning";

interface ErrorDisplayProps {
  title?: string;
  message?: string;
  error?: string | null;
  onRetry?: () => void;
  retryText?: string;
  className?: string;
  showIcon?: boolean;
  variant?: ErrorDisplayVariant;
  /** Viewport overlay for boot/auth failures. Default is an inline themed card. */
  fullPage?: boolean;
}

const VARIANT_STYLES: Record<
  ErrorDisplayVariant,
  { icon: LucideIcon; iconWrap: string; accent: string }
> = {
  default: {
    icon: AlertTriangle,
    iconWrap: "bg-warning/10 text-warning",
    accent: "border-t-warning/40"
  },
  destructive: {
    icon: AlertCircle,
    iconWrap: "bg-destructive/10 text-destructive",
    accent: "border-t-destructive/40"
  },
  warning: {
    icon: AlertTriangle,
    iconWrap: "bg-warning/10 text-warning",
    accent: "border-t-warning/40"
  }
};

export function ErrorDisplay({
  title = "An error occurred",
  message = "Something went wrong. Please try again.",
  error,
  onRetry,
  retryText = "Retry",
  className,
  showIcon = true,
  variant = "default",
  fullPage = false
}: ErrorDisplayProps) {
  const styles = VARIANT_STYLES[variant];
  const Icon = styles.icon;

  const card = (
    <Card
      flush
      role="alert"
      className={cn(
        "w-full border-t-2",
        styles.accent,
        fullPage && "max-w-md",
        !fullPage && className
      )}
    >
      <CardContent className="flex flex-col items-center justify-center gap-4 px-4 py-10 text-center sm:gap-5 sm:px-8 sm:py-14">
        {showIcon && (
          <div
            className={cn(
              "flex size-12 shrink-0 items-center justify-center rounded-full sm:size-14",
              styles.iconWrap
            )}
          >
            <Icon className="size-6 sm:size-7" aria-hidden />
          </div>
        )}

        <div className="flex w-full max-w-md flex-col gap-1.5">
          <h2 className="text-base font-semibold tracking-tight text-foreground sm:text-lg">
            {title}
          </h2>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {message}
          </p>
          {error && (
            <p className="mt-1 break-words rounded-md bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
              {error}
            </p>
          )}
        </div>

        {onRetry && (
          <Button
            type="button"
            onClick={onRetry}
            variant="outline"
            className="w-full sm:w-auto"
          >
            <RefreshCw className="size-4" />
            {retryText}
          </Button>
        )}
      </CardContent>
    </Card>
  );

  if (fullPage) {
    return (
      <div
        className={cn(
          "fixed inset-0 z-50 flex items-center justify-center bg-background p-4",
          className
        )}
      >
        {card}
      </div>
    );
  }

  return card;
}
